import { HttpClient } from "@angular/common/http";
import { inject, Inject, Injectable } from "@angular/core";
import { EnvConfig, Environment } from "@doc/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiUrls } from "../backoffice-urls";

@Injectable({
  providedIn: "root",
})
export class ICDDepartmentDashboardService {
  private http = inject(HttpClient);
  private apiUrl: string;

  constructor(@Inject(EnvConfig) private config: Environment) {
    this.apiUrl = this.config.apiUrl;
  }

  private _headerDataSubject = new BehaviorSubject<any>(null);
  headerData$ = this._headerDataSubject.asObservable();

  private _typeDataSubject = new BehaviorSubject<any>(null);
  typeData$ = this._typeDataSubject.asObservable();

  private _secondHeaderDataSubject = new BehaviorSubject<{
    left: any;
    right: any;
  }>({
    left: null,
    right: null,
  });
  secondHeaderDataSubject$ = this._secondHeaderDataSubject.asObservable();

  private _chartDataSubject = new BehaviorSubject<any>(null);
  chartDataSubject$ = this._chartDataSubject.asObservable();

  setChartData(data: any) {
    this._chartDataSubject.next(data);
  }

  get currentChartData() {
    return this._chartDataSubject.value;
  }

  setSecondHeaderDataLeft(data: any) {
    const current = this._secondHeaderDataSubject.value;
    this._secondHeaderDataSubject.next({ ...current, left: data });
    this.setChartData(null);
  }

  setSecondHeaderDataRight(data: any) {
    const current = this._secondHeaderDataSubject.value;
    this._secondHeaderDataSubject.next({ ...current, right: data });
    this.setChartData(null);
  }
  
  get currentSecondHeaderDataLeft() {
    return this._secondHeaderDataSubject.value.left;
  }

  get currentSecondHeaderDataRight() {
    return this._secondHeaderDataSubject.value.right;
  }

  setHeaderData(data: any) {
    this._headerDataSubject.next(data);
    this.setTypeData(null);
  }

  get currentHeaderData() {
    return this._headerDataSubject.value;
  }

  setTypeData(data: any) {
    this._typeDataSubject.next(data);
    this.setSecondHeaderDataLeft(null);
    this.setSecondHeaderDataRight(null);
  }

  get currentTypeData() {
    return this._typeDataSubject.value;
  }

  GetPrecedentDashboardCounts(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeCounts;
    return this.http.post<any>(link, body);
  }

  GetRecommendationsCount(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeRecommendationsCount;
    return this.http.post<any>(link, body);
  }

  GetParticipantNamesPerDep(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeParticipantNamesPerDep;
    return this.http.post<any>(link, body);
  }

  GetPrecedentCategoryTwoCounts(body: any) {
    const link =
      this.apiUrl + ApiUrls.GetDashboardThreePrecedentCategoryTwoCounts;
    return this.http.post<any>(link, body);
  }

  GetStatusCounts(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeStatusCount;
    return this.http.post<any>(link, body);
  }

  GetPrecedentDocumentDetails(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeDocumentDetails;
    return this.http.post<any>(link, body);
  }

  getParticipantsAccordionData(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeDocumentPomDetails;
    return this.http.post<any>(link, body);
  }

  GetRecommendationDetails(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeRecommendationDetails;
    return this.http.post<any>(link, body);
  }

  GetDocumentWorkFlowDetails(body: any) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeDocumentWorkFlowDetails;
    return this.http.post<any>(link, body);
  }

  GetParticipationMinutesPerDep(body: any) {
    const link =
      this.apiUrl + ApiUrls.GetDashboardThreeParticipationMinutesPerDep;
    return this.http.post<any>(link, body);
  }

  GetDashboardTwoParticipantPerDocument(body: any, isPart: boolean) {
    const link = isPart
      ? this.apiUrl + ApiUrls.GetDashboardThreeParticipantPerDocument
      : this.apiUrl + ApiUrls.GetDashboardThreeDocumentCountPerEmployee;
    return this.http.post<any>(link, body);
  }

  GetPrecedentCategoryTwoMonthlyCounts(body: any) {
    const link =
      this.apiUrl + ApiUrls.GetDashboardThreePrecedentCategoryTwoMonthlyCounts;
    return this.http.post<any>(link, body);
  }

  FilterDropDownValues(id: any) {
    const link =
      this.apiUrl +
      ApiUrls.GetDashboardThreePrecedentDashboardFilterDropDownValues +
      "?" +
      id;
    return this.http.get<any>(link);
  }

  GetDashboardSubject(id: number) {
    const link = this.apiUrl + ApiUrls.GetDashboardThreeSubject + "/" + id;
    return this.http.get<any>(link);
  }

  ExportReport(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.ExportDashboardThreeData;
    return this.http.post(link, body, { responseType: "blob" });
  }

  GetDashboardThreeDocumentCountPerEmployee(body: any) {
    const link =
      this.apiUrl + ApiUrls.GetDashboardThreeDocumentCountPerEmployee;
    return this.http.post<any>(link, body);
  }

  GetCountDashboardThreeEmployeeResponsibleTranslations(body: any) {
    const link =
      this.apiUrl +
      ApiUrls.GetCountDashboardThreeEmployeeResponsibleTranslations;
    return this.http.post<any>(link, body);
  }

  ExportDashboardThreeDocumentWorkFlowDetails(body: any): Observable<any> {
    const link =
      this.apiUrl + ApiUrls.ExportDashboardThreeDocumentWorkFlowDetails;
    return this.http.post(link, body, { responseType: "blob" });
  }
}
